import { useEffect, useSyncExternalStore } from 'react'
import { api } from '../services/API'
import { useAuth } from '../contexts/AuthContext'
import type { AcessoPrestacaoContas } from '../utils/types'

/**
 * O que o utilizador pode ver e fazer numa propriedade (estate PROPERTIES) — `GET /api/prestacao-contas/acesso`:
 * se é da gestora (extractos de todos os proprietários; gerir honorários e entregas) e se é proprietário (vê
 * as finanças da propriedade toda, só para leitura, e o seu extracto). Num condomínio vem tudo a falso.
 *
 * Um só pedido por utilizador + estate, partilhado pelo menu, pelos guards de rota e pelas páginas; o
 * AuthContext descarta-o no logout (`invalidatePropertyAccess`).
 */
type Status = 'loading' | 'ready' | 'error'
interface Snapshot { key: string | null; status: Status; data: AcessoPrestacaoContas | null }

const NONE: AcessoPrestacaoContas = { propriedade: false, gestora: false, podeGerir: false, proprietario: false, temExtracto: false }
const EMPTY: Snapshot = { key: null, status: 'loading', data: null }

let snapshot: Snapshot = EMPTY
const listeners = new Set<() => void>()
const publish = (next: Snapshot) => {
  snapshot = next
  listeners.forEach((l) => l())
}

function load(key: string) {
  if (snapshot.key === key && snapshot.status !== 'error') return
  publish({ key, status: 'loading', data: null })
  api.get<AcessoPrestacaoContas>('/api/prestacao-contas/acesso', { suppressForbiddenToast: true })
    .then((r) => { if (snapshot.key === key) publish({ key, status: 'ready', data: r.data ?? NONE }) })
    .catch(() => { if (snapshot.key === key) publish({ key, status: 'error', data: NONE }) })
}

/** Descarta a resposta — no logout, e depois de mudar as associações de uma unidade. */
export function invalidatePropertyAccess() {
  publish(EMPTY)
}

/**
 * @param enabled só pergunta numa propriedade — num condomínio devolve logo "nada" sem pedido.
 * @returns `status` ('loading' enquanto não há resposta) e o acesso (tudo a falso enquanto carrega).
 */
export function usePropertyAccess(enabled: boolean): { status: Status; access: AcessoPrestacaoContas } {
  const { user } = useAuth()
  const current = useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l) } },
    () => snapshot,
    () => snapshot,
  )
  const key = enabled && user?.id && user.estateId ? `${user.id}:${user.estateId}` : null

  useEffect(() => {
    if (key) load(key)
  }, [key])

  if (!key) return { status: 'ready', access: NONE }
  if (current.key !== key || current.status === 'loading') return { status: 'loading', access: NONE }
  return { status: current.status, access: current.data ?? NONE }
}
